import { Controller } from '@hotwired/stimulus';

export default class SedeCurrencyController extends Controller {
    static targets = ['country', 'mask', 'phonePrefixPreview', 'maskTest', 'maskPreview', 'currency', 'currencyDisplay', 'symbol', 'symbolPreview', 'decimalSeparator', 'thousandSeparator', 'decimalPlaces', 'preview', 'currencyTest'];

    declare readonly countryTarget: HTMLSelectElement;
    declare readonly maskTarget: HTMLInputElement;
    declare readonly phonePrefixPreviewTarget: HTMLElement;
    declare readonly maskTestTarget: HTMLInputElement;
    declare readonly maskPreviewTarget: HTMLElement;
    declare readonly currencyTarget: HTMLSelectElement;
    declare readonly currencyDisplayTargets: HTMLInputElement[];
    declare readonly symbolTarget: HTMLInputElement;
    declare readonly symbolPreviewTargets: HTMLElement[];
    declare readonly decimalSeparatorTarget: HTMLInputElement;
    declare readonly thousandSeparatorTarget: HTMLInputElement;
    declare readonly decimalPlacesTarget: HTMLInputElement;
    declare readonly previewTarget: HTMLElement;
    declare readonly currencyTestTarget: HTMLInputElement;
    declare readonly hasPhonePrefixPreviewTarget: boolean;
    declare readonly hasMaskTestTarget: boolean;
    declare readonly hasMaskPreviewTarget: boolean;
    declare readonly hasSymbolPreviewTarget: boolean;
    declare readonly hasPreviewTarget: boolean;
    declare readonly hasCurrencyTestTarget: boolean;

    connect(): void {
        this.updatePreview();
        this.applyMaskTest();
    }

    syncCountry(): void {
        const option = this.countryTarget.selectedOptions[0];
        if (!option) return;

        const currency = option.dataset.currency || '';
        const symbol = option.dataset.symbol || '';
        const mask = option.dataset.mask || '';
        const decimalSeparator = option.dataset.decimalSeparator || '.';
        const thousandSeparator = option.dataset.thousandSeparator || ',';
        const decimalPlaces = option.dataset.decimalPlaces || '2';

        this.maskTarget.value = mask;
        this.currencyTarget.value = currency;
        this.currencyDisplayTargets.forEach((target) => {
            target.value = currency;
        });
        this.symbolTarget.value = symbol;
        this.decimalSeparatorTarget.value = decimalSeparator;
        this.thousandSeparatorTarget.value = thousandSeparator;
        this.decimalPlacesTarget.value = decimalPlaces;

        if (this.hasPhonePrefixPreviewTarget) {
            this.phonePrefixPreviewTarget.textContent = `+${this.countryTarget.value}`;
        }
        if (this.hasMaskTestTarget) { this.maskTestTarget.value = ''; }
        if (this.hasMaskPreviewTarget) { this.maskPreviewTarget.textContent = '—'; }

        this.updatePreview();
    }

    updateMaskPreview(): void {
        this.applyMaskTest();
    }

    updatePreview(): void {
        if (!this.hasPreviewTarget) return;

        const decimalSep  = this.decimalSeparatorTarget.value  || '.';
        const thousandSep = this.thousandSeparatorTarget.value || ',';
        const rawPlaces   = parseInt(this.decimalPlacesTarget.value, 10);
        const places      = Number.isNaN(rawPlaces) ? 2 : rawPlaces;
        const symbol      = this.symbolTarget.value || '';

        const sample  = 1234567.891;
        const [intPart, decPart] = sample.toFixed(Math.max(places, 3)).split('.');

        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
        const formattedDec = places > 0 ? decimalSep + decPart.slice(0, places) : '';

        this.previewTarget.textContent = formattedInt + formattedDec;
        if (this.hasSymbolPreviewTarget) {
            this.symbolPreviewTargets.forEach((target) => {
                target.textContent = symbol;
            });
        }
    }

    /** Called when user types in the mask test input. */
    applyMaskTest(): void {
        if (!this.hasMaskTestTarget || !this.hasMaskPreviewTarget) return;

        const digits = this.maskTestTarget.value.replace(/\D/g, '');
        const mask = this.pickMask(this.getPhoneMasks(), digits.length);
        const formatted = this.formatWithMask(mask, digits);

        this.maskTestTarget.value = formatted;
        this.maskPreviewTarget.textContent = formatted || '—';
    }

    /** Called when user types in the currency test input. */
    applyCurrencyTest(): void {
        if (!this.hasCurrencyTestTarget || !this.hasPreviewTarget) return;

        const raw = this.currencyTestTarget.value.trim();
        const num = parseFloat(raw);

        if (!raw || Number.isNaN(num)) {
            this.updatePreview();
            return;
        }

        const decimalSep  = this.decimalSeparatorTarget.value || '.';
        const thousandSep = this.thousandSeparatorTarget.value || ',';
        const rawPlaces   = parseInt(this.decimalPlacesTarget.value, 10);
        const places      = Number.isNaN(rawPlaces) ? 2 : rawPlaces;

        const [intPart, decPart] = num.toFixed(Math.max(places, 0)).split('.');
        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
        const formattedDec = places > 0 ? decimalSep + (decPart ?? '').slice(0, places) : '';

        this.previewTarget.textContent = formattedInt + formattedDec;
        if (this.hasSymbolPreviewTarget) {
            this.symbolPreviewTargets.forEach((t) => {
                t.textContent = this.symbolTarget.value || '';
            });
        }
    }

    private formatWithMask(mask: string, digits: string): string {
        if (mask === '') return digits;

        let formatted = '';
        let index = 0;

        for (const char of mask) {
            if (char === '#') {
                if (index >= digits.length) break;
                formatted += digits[index];
                index++;
            } else {
                // Include separator characters even if no more digits follow,
                // only while there are still digits left to place
                if (index < digits.length) {
                    formatted += char;
                }
            }
        }

        return formatted;
    }

    /** El mask-test previsualiza contra la máscara propia + los formatos alternativos ya
     *  conocidos para el país elegido (ej. Brasil: número nuevo y viejo), sin necesidad
     *  de un campo de texto propio para esos extras — vienen del catálogo de países. */
    private getPhoneMasks(): string[] {
        const option = this.countryTarget.selectedOptions[0];
        const catalogExtras = (option?.dataset.masksExtra || '')
            .split(',')
            .map((mask) => mask.trim())
            .filter((mask) => mask !== '');

        return [this.maskTarget.value, ...catalogExtras]
            .map((mask) => mask.trim())
            .filter((mask) => mask !== '');
    }

    private pickMask(masks: string[], digitsLength: number): string {
        if (masks.length === 0) return '';

        const sortedMasks = [...masks].sort((a, b) => this.countMaskDigits(a) - this.countMaskDigits(b));
        const exactMask = sortedMasks.find((mask) => this.countMaskDigits(mask) === digitsLength);
        if (exactMask) return exactMask;

        return sortedMasks.find((mask) => this.countMaskDigits(mask) >= digitsLength) ?? sortedMasks[sortedMasks.length - 1];
    }

    private countMaskDigits(mask: string): number {
        return (mask.match(/#/g) ?? []).length;
    }
}
